import React from "react";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import HelpIcon from "@mui/icons-material/Help";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useNavigate } from "react-router-dom";

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: "4px",
  padding: "12px 20px",
  marginBottom: "8px",
  backgroundColor: "#f5f5f5",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  transition: "background-color 0.3s",
  "&:hover": {
    backgroundColor: "#e0e0e0",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  color: "white",
  backgroundColor: "#319B42",
  boxShadow: "none",
  borderRadius: "9999px",  // <- make the button fully rounded
  padding: "8px 40px",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#319B42",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const HtmlTooltip = ({ title, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = React.useState(false);

  const handleTooltipOpen = () => {
    if (isMobile) {
      setOpen(true);
    }
  };

  const handleTooltipClose = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  const child = React.Children.toArray(children)[0];

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        title={title}
        open={isMobile ? open : undefined}
        onOpen={isMobile ? handleTooltipOpen : undefined}
        onClose={isMobile ? handleTooltipClose : undefined}
        disableHoverListener={isMobile}
        disableFocusListener={isMobile}
        disableTouchListener={false}
      >
        <span onClick={handleTooltipOpen}>{child}</span>
      </Tooltip>
    </ClickAwayListener>
  );
};

const EnrollData = ({
  approvedVendors,
  planTypes,
  employerName,
  plans,
  empState,
  reliusWeb,
  employerId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const handleClick = (pin) => {
    const url = `https://${reliusWeb}/enrollinplan.aspx`;
    const formData = {
      LoginButton: "true",
      RANDOMNUM: "",
      PLANID: "",
      PIN: pin,
      DROP: "",
    };
    post(url, formData);
  };

  function post(path, params, method = "post", target = "_blank") {
    const form = document.createElement("form");
    form.method = method;
    form.action = path;
    form.target = target; // <-- This ensures the form submission opens in a new tab
  
    for (const key in params) {
      if (Object.hasOwnProperty.call(params, key)) {
        const hiddenField = document.createElement("input");
        hiddenField.type = "hidden";
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
    }
  
    document.body.appendChild(form);
    form.submit();
  }

  const handleByPass = (planName) => {
    const matchingPlan = plans.find(
      (item) =>
        item.planUniqueId &&
        item.planUniqueId.split("_")[1] === planName.replace(/([() ])/g, "")
    );
    if (matchingPlan) {
      const passwordData =
        matchingPlan.planData &&
        matchingPlan.planData.find((data) => data.field === "planPassword");
      const pin = passwordData ? passwordData.value : "";
      handleClick(pin);
    }
  };

  const ButtonSelection = (planLabel) => {
    return (
      <StyledButton
        fullWidth={isMobile}
        disableElevation
        variant="contained"
        onClick={() => {
          if (planLabel === "403(b) Admin") {
            // Pass reliusWeb along via state when navigating to ApprovedVendors
            navigate(
              `/search-enroll/${employerId}/${empState}/approved-vendor?planType=${encodeURIComponent(
                planLabel
              )}`,
              { state: { reliusWeb } }
            );
          } else {
            handleByPass(planLabel);
          }
        }}
        aria-label={`Enroll in ${planLabel}`}
      >
        {/* Desktop & Mobile change #2: Uppercase “ENROLL” */}
        ENROLL
      </StyledButton>
    );
  };

  const getPlanDescription = (planType) => {
    const matchingPlan = plans.find(
      (plan) => plan.code === planType || plan.label === planType
    );
    if (matchingPlan) {
      return matchingPlan.planDesc;
    } else {
      console.warn("No matching plan found for planType:", planType);
      return "Retirement plan information not found.";
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        variant="h6"
        sx={{
          mb: isMobile ? 2 : 3,
          textAlign: "center",
          fontWeight: "bold",
          whiteSpace: "nowrap",
          overflow: "hidden",
          fontSize: isMobile ? ".75rem" : "1.25rem",
        }}
      >
        Plans for {employerName}:
      </Typography>

      <List sx={{ px: isMobile ? 1 : 0 }}>
        {planTypes &&
          planTypes.map((item, index) => (
            <StyledListItem
              key={index}
              sx={{ flexDirection: isMobile ? "column" : "row" }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center">
                    <span>{`${item.label} Savings Plan`}</span>
                    <HtmlTooltip
                      title={<span>{getPlanDescription(item.label)}</span>}
                    >
                      <span>
                        <HelpIcon
                          color="primary"
                          sx={{
                            fontSize: "16px",
                            marginLeft: "8px",
                            cursor: "pointer",
                          }}
                          aria-label={`More information about ${item.label}`}
                        />
                      </span>
                    </HtmlTooltip>
                  </Box>
                }
                sx={{
                  textAlign: isMobile ? "center" : "left",
                  mb: isMobile ? 1 : 0,
                }}
              />
              <ListItemSecondaryAction
                sx={{
                  position: isMobile ? "relative" : "absolute",
                  right: isMobile ? 0 : "16px",
                  width: isMobile ? "100%" : "auto",
                  textAlign: "right",
                }}
              >
                {ButtonSelection(item.label)}
              </ListItemSecondaryAction>
            </StyledListItem>
          ))}
      </List>
    </Box>
  );
};

export default EnrollData;
